import React, { useEffect, useRef, useState, useMemo } from "react";
import { FaFacebook, FaVimeoV, FaTwitter, FaLinkedin, FaBars, FaTimes } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { IoMdCart } from "react-icons/io";
import { Layout } from "antd";
import { gsap } from "gsap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AuthModal from "./modals/authModal";
import { useCart } from "../context/CartContext";

const { Header } = Layout;

const TopBar = () => {
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cartItems, designCart } = useCart();

  const mergedCartCount = useMemo(
    () => cartItems.length + designCart.length,
    [cartItems, designCart]
  );

  // 🗂️ Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get("/api/categories/fetch", {
          params: { isActive: "true", page: 1, limit: 100 },
        });
        if (response.data.success) {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // ✨ GSAP Animations - Desktop only
  useEffect(() => {
    if (!loading && window.innerWidth >= 1024) {
      const ctx = gsap.context(() => {
        gsap.from(".topbar-header", { y: -80, opacity: 0, duration: 1 });
        gsap.from(".social-icon", {
          opacity: 0,
          y: -20,
          duration: 0.8,
          stagger: 0.2,
          delay: 0.5,
        });
        gsap.from(".main-search-bar", {
          scale: 0.8,
          opacity: 0,
          duration: 1,
          delay: 0.8,
        });
        gsap.from(".nav-link", {
          opacity: 0,
          y: 20,
          duration: 0.8,
          stagger: 0.15,
          delay: 1,
        });
      }, headerRef);
      return () => ctx.revert();
    }
  }, [loading]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleCategoryClick = (e, slug) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    navigate(`/categories/${slug}`);
  };

  const handleNavClick = (href) => {
    setMobileMenuOpen(false);
    navigate(href);
  };

  return (
    <>
      <Header
        ref={headerRef}
        className="!h-auto bg-[#6CC5BF] px-0 py-0 topbar-header relative z-30"
      >
        {/* Top Bar - Desktop Only */}
        <div className="container mx-auto hidden lg:block">
          <ul className="flex justify-between bg-[#6CC5BF] items-center text-white space-x-4 py-2 px-4">
            <li className="text-sm xl:text-lg font-custom social-icon">
              Welcome to Initial Global Trade!
            </li>
            <li className="text-sm xl:text-lg font-custom social-icon">
              Free shipping on all U.S. orders $50+
            </li>
            <li>
              <ul className="flex items-center gap-3 xl:gap-5 text-lg font-custom">
                <li className="social-icon">
                  <a
                    href="/"
                    className="bg-white text-[#1877F2] p-[6px] rounded-full flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-colors duration-300"
                  >
                    <FaFacebook />
                  </a>
                </li>
                <li className="social-icon">
                  <a
                    href="/"
                    className="bg-white text-[#1ab7ea] p-[6px] rounded-full flex items-center justify-center hover:bg-[#1ab7ea] hover:text-white transition-colors duration-300"
                  >
                    <FaVimeoV />
                  </a>
                </li>
                <li className="social-icon">
                  <a
                    href="/"
                    className="bg-white text-[#0A66C2] p-[6px] rounded-full flex items-center justify-center hover:bg-[#0A66C2] hover:text-white transition-colors duration-300"
                  >
                    <FaLinkedin />
                  </a>
                </li>
                <li className="social-icon">
                  <a
                    href="/"
                    className="bg-white text-[#1DA1F2] p-[6px] rounded-full flex items-center justify-center hover:bg-[#1DA1F2] hover:text-white transition-colors duration-300"
                  >
                    <FaTwitter />
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        {/* 🔍 Search Bar */}
        <div className="main-search-bar bg-white flex justify-between items-center h-16 md:h-20 px-4 md:px-6">
          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden text-2xl text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(true)}
            type="button"
            aria-label="Open menu"
          >
            <FaBars />
          </button>

          {/* Search Input - Desktop */}
          <div className="relative w-full max-w-[900px] mx-auto hidden md:flex items-center justify-center">
            <input
              type="text"
              className="border rounded-3xl px-3 w-full h-12 pr-10 font-custom placeholder:font-custom placeholder:text-gray-400
                     focus:outline-none focus:ring-2 focus:ring-[#6CC5BF] focus:border-[#6CC5BF]"
              placeholder="Search For Products ..."
            />
            <button 
              className="absolute rounded-full p-2 right-2 top-1/2 -translate-y-1/2 bg-[#6CC5BF] text-white text-xl hover:bg-[#5ab4ae] transition-colors"
              aria-label="Search"
            >
              <CiSearch />
            </button>
          </div>

          {/* Mobile Cart & Auth */}
          <div className="flex items-center gap-4 lg:hidden">
            <AuthModal />
            <a href="/cart" className="relative">
              <IoMdCart className="text-black text-2xl hover:text-[#6cc5bf] transition-colors" />
              {mergedCartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#6cc5bf] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {mergedCartCount}
                </span>
              )}
            </a>
          </div>
        </div>

        {/* 🧭 Desktop Navigation */}
        <nav className="hidden lg:flex justify-center items-center bg-[#FCF5FC]">
          <ul className="flex justify-center items-center space-x-4 xl:space-x-6 bg-[#FCF5FC] py-3 font-custom text-base xl:text-lg">
            <li>
              <a
                href="/"
                className="nav-link text-black font-medium hover:text-[#6cc5bf] transition-colors"
              >
                Home
              </a>
            </li>
            <li>
              <a
                href="/designer"
                className="nav-link text-black font-medium hover:text-[#6cc5bf] transition-colors"
              >
                Custom T-Shirts
              </a>
            </li>

            {/* 🏷️ Dynamic Categories */}
            {!loading && categories.length > 0 ? (
              categories.map((category) => (
                <li key={category._id}>
                  <a
                    href={`/categories/${category.slug}`}
                    onClick={(e) => handleCategoryClick(e, category.slug)}
                    className="nav-link text-black font-medium hover:text-[#6cc5bf] transition-colors"
                  >
                    {category.name}
                  </a>
                </li>
              ))
            ) : loading ? (
              <li className="nav-link text-black font-medium">Loading...</li>
            ) : null}

            <li>
              <a
                href="/services"
                className="nav-link text-black font-medium hover:text-[#6cc5bf] transition-colors"
              >
                Services
              </a>
            </li>
            <li>
              <a
                href="/helpcenter"
                className="nav-link text-black font-medium hover:text-[#6cc5bf] transition-colors"
              >
                Help Center
              </a>
            </li>
            <li>
              <ul className="flex items-center gap-4 xl:gap-6 pl-4">
                <AuthModal />

                {/* 🛒 Cart */}
                <li className="nav-link relative">
                  <a href="/cart" className="relative">
                    <IoMdCart className="text-black hover:text-[#6cc5bf] transition-colors text-2xl" />
                    {mergedCartCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-[#6cc5bf] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {mergedCartCount}
                      </span>
                    )}
                  </a>
                </li>

                <li className="nav-link">
                  <a
                    href="/contact"
                    className="text-black hover:text-[#6cc5bf] transition-colors text-lg xl:text-2xl"
                  >
                    Let's Talk
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </Header>

      {/* 📱 Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[999] lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* 📱 Mobile Navigation Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[280px] sm:w-[320px] bg-white z-[1000] transform transition-transform duration-300 ease-in-out lg:hidden overflow-y-auto shadow-2xl ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-6">
          {/* Close Button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 text-2xl text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <FaTimes />
          </button>

          {/* Logo/Title */}
          <div className="mb-6 mt-2">
            <h3 className="text-xl font-bold text-[#6CC5BF] font-custom">Menu</h3>
          </div>

          {/* Mobile Search */}
          <div className="relative mb-6">
            <input
              type="text"
              className="border border-gray-300 rounded-full px-4 w-full h-10 pr-10 font-custom placeholder:font-custom placeholder:text-gray-400
                     focus:outline-none focus:ring-2 focus:ring-[#6CC5BF]"
              placeholder="Search..."
            />
            <button className="absolute rounded-full p-1.5 right-1 top-1/2 -translate-y-1/2 bg-[#6CC5BF] text-white">
              <CiSearch className="text-lg" />
            </button>
          </div>

          {/* Mobile Menu Items */}
          <ul className="space-y-1 font-custom">
            <li>
              <button
                onClick={() => handleNavClick("/")}
                className="w-full text-left block text-black font-medium hover:text-[#6cc5bf] hover:bg-gray-50 py-3 px-3 rounded-lg text-base transition-colors"
              >
                Home
              </button>
            </li>
            <li>
              <button
                onClick={() => handleNavClick("/designer")}
                className="w-full text-left block text-black font-medium hover:text-[#6cc5bf] hover:bg-gray-50 py-3 px-3 rounded-lg text-base transition-colors"
              >
                Custom T-Shirts
              </button>
            </li>

            {/* Categories */}
            {!loading && categories.length > 0 && (
              <>
                <li className="pt-4 pb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase px-3">Categories</span>
                </li>
                {categories.map((category) => (
                  <li key={category._id}>
                    <button
                      onClick={(e) => handleCategoryClick(e, category.slug)}
                      className="w-full text-left block text-black font-medium hover:text-[#6cc5bf] hover:bg-gray-50 py-3 px-3 rounded-lg transition-colors"
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </>
            )}

            <li>
              <button
                onClick={() => handleNavClick("/services")}
                className="w-full text-left block text-black font-medium hover:text-[#6cc5bf] hover:bg-gray-50 py-3 px-3 rounded-lg text-base transition-colors"
              >
                Services
              </button>
            </li>
            <li>
              <button
                onClick={() => handleNavClick("/helpcenter")}
                className="w-full text-left block text-black font-medium hover:text-[#6cc5bf] hover:bg-gray-50 py-3 px-3 rounded-lg text-base transition-colors"
              >
                Help Center
              </button>
            </li>
            <li className="pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={() => handleNavClick("/contact")}
                className="w-full text-left block text-[#6cc5bf] font-semibold hover:bg-gray-50 py-3 px-3 rounded-lg text-base transition-colors"
              >
                Let's Talk
              </button>
            </li>
          </ul>

          {/* Social Icons - Mobile */}
          <div className="flex items-center gap-3 mt-8 pt-6 border-t border-gray-200">
            <a href="/" className="bg-[#1877F2] text-white p-2 rounded-full hover:opacity-80 transition-opacity">
              <FaFacebook />
            </a>
            <a href="/" className="bg-[#1ab7ea] text-white p-2 rounded-full hover:opacity-80 transition-opacity">
              <FaVimeoV />
            </a>
            <a href="/" className="bg-[#0A66C2] text-white p-2 rounded-full hover:opacity-80 transition-opacity">
              <FaLinkedin />
            </a>
            <a href="/" className="bg-[#1DA1F2] text-white p-2 rounded-full hover:opacity-80 transition-opacity">
              <FaTwitter />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default TopBar;